import { useState, useEffect, useRef, useMemo } from "react";
import Layout from "./Layout";
import { Select, Button, List } from "antd";
import { CONFIG } from "../config";
import { SITES } from "../sites";
import useSelectedSite from "../hooks/useSelectedSite";
import { useRobotStatus, getStatusColor } from "../hooks/useRobotStatus";
import {
  sendMoveCommand,
  sendDockCommand,
  sendStopCommand,
  sendUnlockCommand,
  getBusyStatus,
  getLastCommand,
} from "../services/robotApi";
import {
  initializePerspectiveTransform,
  applyPerspectiveTransform,
} from "../utils/perspectiveTransform";
import "./RobotControlPage.css";

const SITE_OPTIONS = SITES;

const RobotControlPage = () => {
  const { selectedSite, setSelectedSite } = useSelectedSite();
  const selectedSiteOption = SITE_OPTIONS.find((s) => s.value === selectedSite);
  const deviceId = selectedSiteOption?.deviceId;
  const robotName = selectedSiteOption?.robotName;
  const robotImage = selectedSiteOption?.robotImage;
  const { robotData } = useRobotStatus(deviceId, true);
  const [selectedCommand, setSelectedCommand] = useState({
    poiId: "",
    name: "",
    command: "",
  });
  const [markerPos, setMarkerPos] = useState({ x: 0, y: 0, deg: 0 });
  const [targetPos, setTargetPos] = useState({ x: 0, y: 0, deg: 0 });
  const [imageMetrics, setImageMetrics] = useState({
    scaleX: 1,
    scaleY: 1,
    offsetX: 0,
    offsetY: 0,
  });
  const [isCommandPending, setIsCommandPending] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const prevStatusRef = useRef(null);
  const prevIsBusyRef = useRef(null);
  const transformMatrixRef = useRef(null);
  const animationFrameRef = useRef(null);
  const mapImageRef = useRef(null);

  // Initialize perspective transform
  useEffect(() => {
    if (selectedSiteOption?.perspectiveMapping) {
      transformMatrixRef.current = initializePerspectiveTransform(
        selectedSiteOption.perspectiveMapping,
      );
    }
  }, [selectedSite]); // eslint-disable-line react-hooks/exhaustive-deps

  // 이미지 크기 변화 감지 및 스케일/오프셋 계산
  useEffect(() => {
    const updateImageMetrics = () => {
      if (mapImageRef.current) {
        const img = mapImageRef.current;
        const containerWidth = img.clientWidth;
        const containerHeight = img.clientHeight;
        const { width: originalWidth, height: originalHeight } =
          selectedSiteOption?.mapOriginalSize ?? CONFIG.MAP_ORIGINAL_SIZE;

        // 이미지의 실제 렌더링 크기 계산 (object-fit: contain 고려)
        const originalRatio = originalWidth / originalHeight;
        const containerRatio = containerWidth / containerHeight;

        let renderedWidth, renderedHeight, offsetX, offsetY;

        if (containerRatio > originalRatio) {
          // 컨테이너가 더 넓음 - 높이에 맞춤
          renderedHeight = containerHeight;
          renderedWidth = containerHeight * originalRatio;
          offsetX = (containerWidth - renderedWidth) / 2;
          offsetY = 0;
        } else {
          // 컨테이너가 더 높음 - 너비에 맞춤
          renderedWidth = containerWidth;
          renderedHeight = containerWidth / originalRatio;
          offsetX = 0;
          offsetY = (containerHeight - renderedHeight) / 2;
        }

        setImageMetrics({
          scaleX: renderedWidth / originalWidth,
          scaleY: renderedHeight / originalHeight,
          offsetX,
          offsetY,
        });
      }
    };

    // 초기 로드 시 계산
    updateImageMetrics();

    // 이미지 로드 완료 후 계산
    const imgElement = mapImageRef.current;
    if (imgElement) {
      imgElement.addEventListener("load", updateImageMetrics);
    }

    // 윈도우 리사이즈 시 재계산
    window.addEventListener("resize", updateImageMetrics);

    return () => {
      if (imgElement) {
        imgElement.removeEventListener("load", updateImageMetrics);
      }
      window.removeEventListener("resize", updateImageMetrics);
    };
  }, [selectedSite]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update robot position with easing
  useEffect(() => {
    if (robotData && robotData.location && transformMatrixRef.current) {
      const x = parseFloat(robotData.location.x);
      const y = parseFloat(robotData.location.y);
      const theta = parseFloat(robotData.location.theta);

      if (!isNaN(x) && !isNaN(y) && !isNaN(theta)) {
        const originalPoint = { x, y };
        const transformedPoint = applyPerspectiveTransform(
          originalPoint,
          transformMatrixRef.current,
        );

        setTargetPos({
          x: transformedPoint.x,
          y: transformedPoint.y,
          deg: -theta + 90 + (selectedSiteOption?.headingOffset ?? 0),
        });
      }
    }
  }, [robotData, selectedSiteOption?.headingOffset]);

  // Smooth animation with performance optimization
  useEffect(() => {
    let lastTime = performance.now();
    const minFrameTime = 16; // ~60fps 제한

    const animate = (currentTime) => {
      const deltaTime = currentTime - lastTime;

      // 프레임 드롭 방지: 너무 빠르게 실행되는 것 방지
      if (deltaTime < minFrameTime) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      lastTime = currentTime;

      setMarkerPos((prev) => {
        const dx = targetPos.x - prev.x;
        const dy = targetPos.y - prev.y;
        const ddeg = targetPos.deg - prev.deg;

        // 변화가 거의 없으면 업데이트 중지 (성능 최적화)
        const threshold = 0.1;
        if (
          Math.abs(dx) < threshold &&
          Math.abs(dy) < threshold &&
          Math.abs(ddeg) < 1
        ) {
          return prev;
        }

        // easing 적용 (deltaTime 기반으로 부드럽게)
        const easing = CONFIG.MARKER_RENDER_EASING;
        return {
          x: prev.x + dx * easing,
          y: prev.y + dy * easing,
          deg: prev.deg + ddeg * easing,
        };
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [targetPos]);

  // 스케일 및 오프셋 적용된 마커 위치 계산 (메모이제이션으로 최적화)
  const scaledMarkerPos = useMemo(
    () => ({
      x: markerPos.x * imageMetrics.scaleX + imageMetrics.offsetX,
      y: markerPos.y * imageMetrics.scaleY + imageMetrics.offsetY,
      deg: markerPos.deg,
    }),
    [
      markerPos.x,
      markerPos.y,
      markerPos.deg,
      imageMetrics.scaleX,
      imageMetrics.scaleY,
      imageMetrics.offsetX,
      imageMetrics.offsetY,
    ],
  );
  const status = robotData?.status ?? "OFFLINE";
  const target = robotData?.errorMessage || "-";

  // Parse robot status

  // const status =
  //   robotData?.connectionStatus === "DISCONNECTED"
  //     ? "DISCONNECTED"
  //     : robotData && robotData.isOnline
  //       ? parseRobotStatus(robotData.statusText)
  //       : "OFFLINE";
  // const target = robotData ? parseRobotTarget(robotData.statusText) : "-";

  const statusColor = getStatusColor(status);

  // status 변화 감지 → pending 해제
  useEffect(() => {
    const currentStatus = robotData?.status ?? "OFFLINE";
    if (
      prevStatusRef.current !== null &&
      prevStatusRef.current !== currentStatus
    ) {
      setIsCommandPending(false);
    }
    prevStatusRef.current = currentStatus;
  }, [robotData?.status]);

  // 단지 변경 시 pending·작업 선택·마커 위치 초기화
  useEffect(() => {
    setIsCommandPending(false);
    setIsBusy(false);
    setSelectedCommand({ name: null, poiId: null, command: null });
    setMarkerPos({ x: 0, y: 0, deg: 0 });
    setTargetPos({ x: 0, y: 0, deg: 0 });
    prevStatusRef.current = null;
    prevIsBusyRef.current = null;
  }, [selectedSite]);

  // /busy 폴링: 버튼 비활성 판단 + 작업 완료 시 셀렉트 초기화 + 첫 폴링 시 last-command 복원
  useEffect(() => {
    if (!deviceId) return;

    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchBusy = async () => {
      try {
        const busy = await getBusyStatus(deviceId, signal);
        if (signal.aborted) return;

        setIsBusy(busy);

        // ref를 먼저 업데이트해야 중복 호출 방지 (concurrent poll 방어)
        const prev = prevIsBusyRef.current;
        prevIsBusyRef.current = busy;

        if (prev === null && busy) {
          try {
            const lastCmd = await getLastCommand(deviceId, signal);
            if (!signal.aborted && lastCmd?.poiId) {
              const poiList = selectedSiteOption?.poiList ?? CONFIG.POI_LIST;
              const isUndock = lastCmd.command === "COMMAND_UNDOCK";
              const poi = poiList.find(
                (p) =>
                  p.poiId === lastCmd.poiId &&
                  (isUndock
                    ? p.name.includes("언도킹")
                    : !p.name.includes("언도킹")),
              );
              if (poi) setSelectedCommand({ name: poi.name, poiId: poi.poiId });
            }
          } catch {
            // 404 등 last-command 없는 경우 무시
          }
        }

        if (prev === true && !busy) {
          setIsCommandPending(false);
          setSelectedCommand({ name: null, poiId: null, command: null });
        }
      } catch (err) {
        if (signal.aborted) return;
        console.error("Error fetching busy status:", err);
      }
    };

    fetchBusy();
    const interval = setInterval(fetchBusy, CONFIG.POLLING_INTERVAL);
    return () => {
      abortController.abort();
      clearInterval(interval);
    };
  }, [deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMove = async () => {
    if (!selectedCommand.name) {
      alert("목적지를 선택해주세요.");
      return;
    }
    if (
      robotData?.liftStatus === 0 &&
      (selectedCommand.command === "COMMAND_MOVE" ||
        selectedCommand.command === "COMMAND_UNDOCK")
    ) {
      alert(
        "이동 또는 언도킹을 위해서는 도킹이 완료되어야 합니다.\n도킹 명령을 먼저 실행해주세요.",
      );
      return;
    }
    setIsCommandPending(true);
    try {
      if (selectedCommand.command === "COMMAND_DOCK") {
        await sendDockCommand(deviceId);
      } else {
        const commandType =
          selectedSite !== "1" && selectedCommand.name.includes("언도킹")
            ? "COMMAND_UNDOCK"
            : "COMMAND_MOVE";
        await sendMoveCommand(
          deviceId,
          selectedCommand.poiId,
          selectedCommand.name,
          commandType,
        );
      }
      alert(`${selectedCommand.name} 명령이 전송되었습니다.`);
    } catch (err) {
      setIsCommandPending(false);
      alert(`${selectedCommand.name} 명령 전송에 실패했습니다.`);
      console.error(err);
    }
  };

  const handleStop = async () => {
    setIsCommandPending(true);
    try {
      await sendStopCommand(deviceId);
      alert("정지 명령이 전송되었습니다.");
    } catch (err) {
      alert("정지 명령 전송에 실패했습니다.");
      console.error(err);
    } finally {
      setIsCommandPending(false);
    }
  };

  const handleUnlock = async () => {
    setIsCommandPending(true);
    try {
      await sendUnlockCommand(deviceId);
      alert("작업 실행 잠금해제 명령이 전송되었습니다.");
    } catch (err) {
      setIsCommandPending(false);
      alert("작업 실행 잠금해제 명령 전송에 실패했습니다.");
      console.error(err);
    }
  };

  // Button enabled states
  const isMoveDisabled =
    isCommandPending ||
    isBusy ||
    status === "MOVE" ||
    status === "OPERATION" ||
    status === "OFFLINE" ||
    status === "ERROR" ||
    status === "DISCONNECTED" ||
    status === "EMERGENCY";
  const isStopDisabled =
    isCommandPending || status === "OFFLINE" || status === "DISCONNECTED";

  const isUnlockAbled =
    !isCommandPending && status === "ERROR" && robotData?.isUndockingError;
  return (
    <Layout>
      <div className="robot-control-page">
        {/* Site Selector */}
        <div className="site-selector-bar">
          <span className="site-selector-label">작업 단지</span>
          <Select
            value={selectedSite}
            onChange={(value) => setSelectedSite(value)}
            options={SITE_OPTIONS}
            className="site-selector-select"
          />
        </div>

        <div className="robot-control-main">
          {/* MAP Section */}
          <div className="card-section map-section">
            <h3 className="section-title">지도</h3>
            <div className="section-content map-container">
              <img
                ref={mapImageRef}
                src={selectedSiteOption?.mapImage ?? CONFIG.MAP_IMAGE}
                alt="Factory Map"
                className="map-image"
              />
              <div
                className={`robot-marker ${statusColor}`}
                style={{
                  left: `${scaledMarkerPos.x}px`,
                  top: `${scaledMarkerPos.y}px`,
                  transform: `translate(-50%, -50%) rotate(${scaledMarkerPos.deg}deg)`,
                }}
              >
                <div className="marker-aura"></div>
                <div className="marker-circle"></div>
                <div className="marker-direction"></div>
              </div>
            </div>
          </div>

          <div className="info-section">
            {/* STATUS Section */}
            <div className="card-section status-section">
              <h3 className="section-title">상태</h3>
              <div className="section-content status-content">
                <div className="robot-visual">
                  <img src={robotImage} alt="Robot" className="robot-image" />
                  <div className="status-wrapper">
                    <div className={`status-indicator ${statusColor}`}></div>
                    <span className={`status-text status-${statusColor}`}>
                      {status}
                    </span>
                  </div>
                </div>
                {/* 상태 정보 */}
                <List
                  className="status-info"
                  size="small"
                  dataSource={[
                    {
                      label: robotName,
                      value: null,
                    },
                    {
                      label: "위치",
                      value: robotData?.location
                        ? `${parseInt(robotData.location.x)}, ${parseInt(
                            robotData.location.y,
                          )}, ${parseInt(robotData.location.theta)}°`
                        : "-",
                      extra: robotData?.location
                        ? ` → (${Math.round(targetPos.x)}, ${Math.round(
                            targetPos.y,
                          )}, ${Math.round(targetPos.deg)}°)`
                        : null,
                    },
                    {
                      label: "배터리",
                      value:
                        robotData?.battery != null
                          ? `${Number(robotData.battery).toFixed(1)}%`
                          : "-",
                    },
                    {
                      label: "상태",
                      value: `${status}, ${selectedSite === "1" ? (robotData?.statusText ?? "") : (robotData?.connectionStatus ?? "")}`,
                      colorClass: `status-${statusColor}`,
                    },
                    {
                      label: "에러 메시지",
                      value: target,
                    },
                  ]}
                  renderItem={(item) => (
                    <List.Item className="info-row">
                      {item.value === null ? (
                        <span className="label-header">{item.label}</span>
                      ) : (
                        <>
                          <span className="label">{item.label}:</span>
                          <span className={`value ${item.colorClass || ""}`}>
                            {item.value}
                            {item.extra && (
                              <span className="transformed-pos">
                                {item.extra}
                              </span>
                            )}
                          </span>
                        </>
                      )}
                    </List.Item>
                  )}
                />
              </div>
            </div>

            {/* CONTROL Section */}
            <div className="card-section control-section">
              <h3 className="section-title">컨트롤</h3>
              <div className="section-content control-content">
                <div className="control-group">
                  <label className="control-label">작업 선택</label>
                  <Select
                    className="poi-select"
                    value={selectedCommand.name || undefined}
                    onChange={(name) => {
                      const poiList =
                        selectedSiteOption?.poiList ?? CONFIG.POI_LIST;
                      const poi = poiList.find((p) => p.name === name);
                      setSelectedCommand({
                        name: poi.name,
                        poiId: poi.poiId,
                        command: poi.command,
                      });
                    }}
                    disabled={isMoveDisabled}
                    placeholder="로봇이 실행할 작업을 선택해주세요."
                    size="large"
                    style={{ width: "100%" }}
                    options={(
                      selectedSiteOption?.poiList ?? CONFIG.POI_LIST
                    ).map((poi) => ({
                      value: poi.name,
                      label: poi.name,
                    }))}
                  />
                </div>

                <div className="control-buttons">
                  <Button
                    className="control-btn move-btn"
                    onClick={handleMove}
                    disabled={isMoveDisabled}
                    size="large"
                    type="primary"
                  >
                    작업 실행
                  </Button>
                  <Button
                    className="control-btn stop-btn"
                    onClick={handleStop}
                    disabled={isStopDisabled}
                    size="large"
                    danger
                  >
                    정지
                  </Button>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <Button
                    className="control-btn"
                    style={{
                      backgroundColor: isUnlockAbled
                        ? "#003D48"
                        : "rgba(0, 0, 0, 0.04)",
                      borderColor: isUnlockAbled ? "#003D48" : "#d9d9d9",
                      color: isUnlockAbled ? "white" : "rgba(0, 0, 0, 0.25)",
                    }}
                    onClick={handleUnlock}
                    disabled={!isUnlockAbled}
                    size="large"
                    block
                  >
                    작업 실행 잠금해제
                  </Button>
                  <p
                    style={{
                      color: "rgba(0, 0, 0, 0.45)",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    롤테이너 언도킹 실패 후 로봇 상태가 정상으로 복구된 경우에만
                    ‘작업 실행 잠금해제’ 버튼을 클릭해주세요.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RobotControlPage;
